import {
  isText2MobileApp,
  requestNativeOAuthSignIn,
  waitForNativeOAuthBridge,
} from './mobileOAuthBridge';

const GOOGLE_SCRIPT_ID = 'text2-google-gsi-script';
const APPLE_SCRIPT_ID = 'text2-apple-auth-script';
const APPLE_SCRIPT_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const APPLE_RESULT_KEY = 'text2_apple_oauth';
const APPLE_STATE_KEY = 'text2_apple_oauth_state';
const APPLE_MESSAGE_TYPE = 'text2-apple-oauth';
const GOOGLE_NONCE_KEY = 'text2_google_oauth_nonce';
const DEFAULT_GOOGLE_CLIENT_ID = '206914662829-8nkbg65a4r976qtqmc54232es2160jt0.apps.googleusercontent.com';
const DEFAULT_APPLE_CLIENT_ID = 'online.text2.signin';
const DEFAULT_APPLE_REDIRECT_URI = 'https://text2.online/login';

function loadScript(id, src) {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('Script load unavailable'));
  }
  const existing = document.getElementById(id);
  if (existing) {
    return existing.dataset.loaded === 'true'
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
          existing.addEventListener('load', () => resolve(), { once: true });
          existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
        });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

function getGoogleClientId() {
  return process.env.REACT_APP_GOOGLE_CLIENT_ID || DEFAULT_GOOGLE_CLIENT_ID;
}

function getAppleClientId() {
  return process.env.REACT_APP_APPLE_CLIENT_ID || DEFAULT_APPLE_CLIENT_ID;
}

function getAppleRedirectUri() {
  const configured = process.env.REACT_APP_APPLE_REDIRECT_URI || DEFAULT_APPLE_REDIRECT_URI;
  if (configured) {
    return configured;
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/login`;
  }
  return DEFAULT_APPLE_REDIRECT_URI;
}

function isAllowedAppleMessageOrigin(origin) {
  if (typeof window !== 'undefined' && origin === window.location.origin) {
    return true;
  }
  try {
    return origin === new URL(getAppleRedirectUri()).origin;
  } catch {
    return false;
  }
}

export function isGoogleOAuthConfigured() {
  return !!getGoogleClientId();
}

export function isAppleOAuthConfigured() {
  return !!getAppleClientId();
}

export function isOAuthAvailable() {
  return isGoogleOAuthConfigured() || isAppleOAuthConfigured() || isText2MobileApp();
}

function getLoginRedirectUri() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/login`;
  }
  return 'https://text2.online/login';
}

function randomNonce() {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function parseJwtPayload(token) {
  const parts = String(token || '').split('.');
  if (parts.length < 2) {
    return null;
  }
  try {
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isMobileWebBrowser() {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
}

function redirectToGoogleSignIn(clientId) {
  const nonce = randomNonce();
  sessionStorage.setItem(GOOGLE_NONCE_KEY, nonce);
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: getLoginRedirectUri(),
    response_type: 'id_token',
    scope: 'openid email profile',
    nonce,
    prompt: 'select_account',
  });
  window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
  return new Promise(() => {});
}

export function consumeGoogleRedirectIdToken() {
  if (typeof window === 'undefined') {
    return null;
  }

  const hash = window.location.hash || '';
  if (!hash.includes('id_token=') && !hash.includes('error=')) {
    return null;
  }

  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const pathWithQuery = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState(null, '', pathWithQuery);

  const error = params.get('error');
  if (error) {
    throw new Error(error === 'access_denied' ? 'cancelled' : error);
  }

  const idToken = params.get('id_token');
  if (!idToken) {
    return null;
  }

  const expectedNonce = sessionStorage.getItem(GOOGLE_NONCE_KEY);
  sessionStorage.removeItem(GOOGLE_NONCE_KEY);
  const payload = parseJwtPayload(idToken);
  if (expectedNonce && payload?.nonce && payload.nonce !== expectedNonce) {
    throw new Error('Google sign-in nonce mismatch');
  }

  return idToken;
}

export function preloadGoogleSignIn() {
  if (!getGoogleClientId() || typeof document === 'undefined') {
    return Promise.resolve();
  }
  return loadScript(GOOGLE_SCRIPT_ID, 'https://accounts.google.com/gsi/client').catch(() => {});
}

/**
 * Google'ın resmi "Google ile devam et" düğmesini container'a çizer. Tıklayınca açılan
 * pencere kimlik jetonunu doğrudan döndürür; yönlendirme adresi (redirect URI) gerekmez,
 * yalnızca Google Cloud'da "Yetkilendirilmiş JavaScript kaynakları"nda bu alan adı olmalı.
 * One Tap (prompt) FedCM ile birlikte sessizce gösterilmeyebildiği için web'de bu kullanılır.
 */
export async function renderGoogleButton(container, onCredential, locale) {
  const clientId = getGoogleClientId();
  if (!clientId || !container || typeof window === 'undefined') {
    return false;
  }
  try {
    await loadScript(GOOGLE_SCRIPT_ID, 'https://accounts.google.com/gsi/client');
  } catch {
    return false;
  }
  const gid = window.google?.accounts?.id;
  if (!gid) {
    return false;
  }
  gid.initialize({
    client_id: clientId,
    callback: (response) => {
      if (response?.credential) {
        onCredential(response.credential);
      }
    },
    auto_select: false,
  });
  // Düğme gizliyken ölçülemez; diğer giriş düğmeleriyle aynı genişlik için üst kutuya bakılır.
  const measured = Math.floor(container.getBoundingClientRect().width || container.parentElement?.getBoundingClientRect().width || 0);
  const width = Math.min(400, Math.max(200, measured || 300));
  container.innerHTML = '';
  gid.renderButton(container, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'continue_with',
    shape: 'rectangular',
    logo_alignment: 'center',
    width,
    locale: locale || 'tr',
  });
  return true;
}

function signInWithGoogleWeb() {
  const clientId = getGoogleClientId();
  if (!clientId) {
    return Promise.reject(new Error('Google client ID not configured'));
  }

  if (isMobileWebBrowser()) {
    return redirectToGoogleSignIn(clientId);
  }

  return loadScript(GOOGLE_SCRIPT_ID, 'https://accounts.google.com/gsi/client').then(
    () =>
      new Promise((resolve, reject) => {
        if (!window.google?.accounts?.id) {
          redirectToGoogleSignIn(clientId).catch(reject);
          return;
        }

        let settled = false;
        const finish = (fn, value) => {
          if (settled) return;
          settled = true;
          fn(value);
        };

        const fallbackToRedirect = () => {
          if (settled) return;
          settled = true;
          redirectToGoogleSignIn(clientId).catch(reject);
        };

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response) => {
            if (response?.credential) {
              finish(resolve, response.credential);
            } else {
              fallbackToRedirect();
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          ux_mode: 'popup',
        });

        window.google.accounts.id.prompt((notification) => {
          if (!notification) {
            return;
          }
          const skipped =
            (typeof notification.isNotDisplayed === 'function' && notification.isNotDisplayed()) ||
            (typeof notification.isSkippedMoment === 'function' && notification.isSkippedMoment());
          if (skipped) {
            fallbackToRedirect();
          }
        });

        window.setTimeout(() => {
          if (!settled) {
            finish(reject, new Error('Google sign-in timed out'));
          }
        }, 120000);
      }),
  );
}

function createAppleState() {
  const state = randomNonce();
  sessionStorage.setItem(APPLE_STATE_KEY, state);
  return state;
}

function isAppleCancelMessage(message) {
  return /cancel|popup_closed|user_cancelled/i.test(String(message || ''));
}

export function preloadAppleSignIn() {
  if (!getAppleClientId() || typeof document === 'undefined') {
    return Promise.resolve();
  }
  return loadScript(APPLE_SCRIPT_ID, APPLE_SCRIPT_SRC).catch(() => {});
}

export function consumeAppleRedirectResult() {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = sessionStorage.getItem(APPLE_RESULT_KEY);
  if (!raw) {
    return null;
  }
  sessionStorage.removeItem(APPLE_RESULT_KEY);

  let payload;
  try {
    payload = JSON.parse(raw);
  } catch {
    return null;
  }

  const expectedState = sessionStorage.getItem(APPLE_STATE_KEY);
  sessionStorage.removeItem(APPLE_STATE_KEY);

  if (payload?.error) {
    throw new Error(isAppleCancelMessage(payload.error) ? 'cancelled' : payload.error);
  }

  if (expectedState && payload?.state && payload.state !== expectedState) {
    throw new Error('Apple sign-in state mismatch');
  }

  return payload?.idToken || null;
}

function signInWithAppleWeb() {
  const clientId = getAppleClientId();
  if (!clientId) {
    return Promise.reject(new Error('Apple client ID not configured'));
  }

  const usePopup = !isMobileWebBrowser();
  const redirectURI = getAppleRedirectUri();
  const state = createAppleState();

  return loadScript(APPLE_SCRIPT_ID, APPLE_SCRIPT_SRC).then(() => {
    if (!window.AppleID?.auth) {
      throw new Error('Apple sign-in unavailable');
    }

    window.AppleID.auth.init({
      clientId,
      scope: 'name email',
      redirectURI,
      state,
      usePopup,
    });

    if (!usePopup) {
      return window.AppleID.auth.signIn();
    }

    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (fn, value) => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        fn(value);
      };

      const onMessage = (event) => {
        if (!isAllowedAppleMessageOrigin(event.origin)) {
          return;
        }
        if (event.data?.type !== APPLE_MESSAGE_TYPE) {
          return;
        }
        sessionStorage.removeItem(APPLE_RESULT_KEY);
        const payload = event.data.payload || {};
        if (payload.idToken) {
          finish(resolve, payload.idToken);
          return;
        }
        const err = payload.error || 'Apple sign-in failed';
        finish(reject, new Error(isAppleCancelMessage(err) ? 'cancelled' : err));
      };

      window.addEventListener('message', onMessage);

      window.AppleID.auth
        .signIn()
        .then((response) => {
          const idToken = response?.authorization?.id_token;
          if (!idToken) {
            throw new Error('Apple sign-in failed');
          }
          finish(resolve, idToken);
        })
        .catch((err) => {
          const message = err?.error || err?.message || 'Apple sign-in failed';
          finish(reject, new Error(isAppleCancelMessage(message) ? 'cancelled' : message));
        });
    });
  });
}

async function signInWithGoogleNative() {
  await waitForNativeOAuthBridge();
  const result = await requestNativeOAuthSignIn('google');
  if (!result.success || !result.idToken) {
    throw new Error(result.message || 'Google sign-in failed');
  }
  return result.idToken;
}

async function signInWithAppleNative() {
  await waitForNativeOAuthBridge();
  const platform = typeof window !== 'undefined' && window.text2OAuth?.platform;
  if (platform === 'android') {
    return signInWithAppleWeb();
  }
  const result = await requestNativeOAuthSignIn('apple');
  if (!result.success || !result.idToken) {
    throw new Error(result.message || 'Apple sign-in failed');
  }
  return result.idToken;
}

export async function acquireOAuthIdToken(provider) {
  const normalized = provider === 'apple' ? 'apple' : 'google';

  if (isText2MobileApp()) {
    return normalized === 'apple'
      ? signInWithAppleNative()
      : signInWithGoogleNative();
  }

  return normalized === 'apple' ? signInWithAppleWeb() : signInWithGoogleWeb();
}
